import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CreateIcon from '@mui/icons-material/Create';
import axios from 'axios';
import { toast } from 'react-toastify';

const apiBase = 'https://serveravotasautofleet.ccsoftwares.com"';

const formatCurrency = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value ?? '0.00';
  return numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function PackageManagement() {
  const [packages, setPackages] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [pkgName, setPkgName] = useState('');
  const [pkgPrice, setPkgPrice] = useState('0');
  const [pkgDescription, setPkgDescription] = useState('');
  const [pkgActive, setPkgActive] = useState(true);

  const [itemsDialogOpen, setItemsDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageItems, setPackageItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedQty, setSelectedQty] = useState('1');

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/Packages/getPackages`, { params: { includeInactive: 1 } });
      setPackages(res.data || []);
    } catch (e) {
      setPackages([]);
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${apiBase}/Item/getItemDetails`);
      setItems(res.data || []);
    } catch (e) {
      setItems([]);
    }
  };

  useEffect(() => {
    fetchPackages();
    fetchItems();
  }, []);

  const openCreate = () => {
    setEditingPackage(null);
    setPkgName('');
    setPkgPrice('0');
    setPkgDescription('');
    setPkgActive(true);
    setPackageDialogOpen(true);
  };

  const openEdit = (pkg) => {
    setEditingPackage(pkg);
    setPkgName(pkg?.PackageName || '');
    setPkgPrice(pkg?.SellingPrice != null ? String(pkg.SellingPrice) : '0');
    setPkgDescription(pkg?.PackageDescription || '');
    setPkgActive(Number(pkg?.IsActive ?? 1) === 1);
    setPackageDialogOpen(true);
  };

  const savePackage = async () => {
    const PackageName = (pkgName || '').trim();
    if (!PackageName) {
      toast.error('Package name is required');
      return;
    }
    const SellingPrice = Number(pkgPrice) || 0;
    const payload = {
      PackageName,
      PackageDescription: pkgDescription || '',
      SellingPrice,
      IsActive: pkgActive ? 1 : 0,
    };

    try {
      if (editingPackage?.PackageID) {
        await axios.put(`${apiBase}/Packages/updatePackage`, { ...payload, PackageID: editingPackage.PackageID });
        toast.success('Package updated');
      } else {
        await axios.post(`${apiBase}/Packages/createPackage`, payload);
        toast.success('Package created');
      }
      setPackageDialogOpen(false);
      await fetchPackages();
    } catch (e) {
      console.log(e);
      toast.error('Failed to save package');
    }
  };

  const openManageItems = async (pkg) => {
    setSelectedPackage(pkg);
    setSelectedItem(null);
    setSelectedQty('1');
    setItemsDialogOpen(true);
    try {
      const res = await axios.get(`${apiBase}/Packages/getPackageItems/${pkg.PackageID}`);
      setPackageItems(res.data || []);
    } catch (e) {
      setPackageItems([]);
      toast.error('Failed to load package items');
    }
  };

  const addItemToPackage = async () => {
    if (!selectedPackage?.PackageID) return;
    if (!selectedItem?.ItemID) {
      toast.error('Select an item');
      return;
    }
    const Qty = Number(selectedQty) || 1;
    try {
      await axios.post(`${apiBase}/Packages/addPackageItem`, {
        PackageID: selectedPackage.PackageID,
        ItemID: selectedItem.ItemID,
        Qty,
        SortOrder: packageItems.length,
      });
      const res = await axios.get(`${apiBase}/Packages/getPackageItems/${selectedPackage.PackageID}`);
      setPackageItems(res.data || []);
      setSelectedItem(null);
      setSelectedQty('1');
      toast.success('Item added');
    } catch (e) {
      toast.error('Failed to add item');
    }
  };

  const removeItemFromPackage = async (packageItemId) => {
    if (!packageItemId) return;
    try {
      await axios.delete(`${apiBase}/Packages/removePackageItem/${packageItemId}`);
      const res = await axios.get(`${apiBase}/Packages/getPackageItems/${selectedPackage.PackageID}`);
      setPackageItems(res.data || []);
      toast.success('Item removed');
    } catch (e) {
      toast.error('Failed to remove item');
    }
  };

  const visiblePackages = useMemo(() => packages, [packages]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="bold">
          Manage Packages
        </Typography>
        <Button variant="contained" endIcon={<AddCircleOutlineIcon />} onClick={openCreate}>
          Add Package
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: '#000000' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Package</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Price (Rs.)</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Items</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visiblePackages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {loading ? 'Loading...' : 'No packages'}
                </TableCell>
              </TableRow>
            ) : (
              visiblePackages.map((pkg) => (
                <TableRow key={pkg.PackageID}>
                  <TableCell>
                    <div style={{ fontWeight: 700 }}>{pkg.PackageName}</div>
                    {pkg.PackageDescription ? (
                      <div style={{ fontSize: 12, color: '#444' }}>{pkg.PackageDescription}</div>
                    ) : null}
                  </TableCell>
                  <TableCell>Rs. {formatCurrency(pkg.SellingPrice)}</TableCell>
                  <TableCell>{Number(pkg.IsActive ?? 1) === 1 ? 'Active' : 'Inactive'}</TableCell>
                  <TableCell>{pkg.items?.length || 0}</TableCell>
                  <TableCell>
                    <Button size="small" startIcon={<CreateIcon />} onClick={() => openEdit(pkg)}>
                      Edit
                    </Button>
                    <Button size="small" onClick={() => openManageItems(pkg)}>
                      Items
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={packageDialogOpen} onClose={() => setPackageDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingPackage ? 'Update Package' : 'Create Package'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Package Name"
                value={pkgName}
                onChange={(e) => setPkgName(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Price (Rs.)"
                type="number"
                value={pkgPrice}
                onChange={(e) => setPkgPrice(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Status (1=Active, 0=Inactive)"
                type="number"
                value={pkgActive ? 1 : 0}
                onChange={(e) => setPkgActive(Number(e.target.value) === 1)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description (what is included)"
                value={pkgDescription}
                onChange={(e) => setPkgDescription(e.target.value)}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackageDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={savePackage}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={itemsDialogOpen} onClose={() => setItemsDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Package Items - {selectedPackage?.PackageName}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={7}>
              <Autocomplete
                options={items}
                value={selectedItem}
                onChange={(_e, val) => setSelectedItem(val)}
                getOptionLabel={(opt) => opt?.ItemName || ''}
                isOptionEqualToValue={(opt, val) => opt?.ItemID === val?.ItemID}
                renderInput={(params) => <TextField {...params} label="Select Item" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Qty"
                type="number"
                value={selectedQty}
                onChange={(e) => setSelectedQty(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button variant="contained" onClick={addItemToPackage} fullWidth>
                Add
              </Button>
            </Grid>
          </Grid>

          <Box mt={2}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {packageItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No items added
                      </TableCell>
                    </TableRow>
                  ) : (
                    packageItems.map((pi) => (
                      <TableRow key={pi.PackageItemID}>
                        <TableCell>{pi.ItemName || '-'}</TableCell>
                        <TableCell>{pi.Qty}</TableCell>
                        <TableCell align="right">
                          <IconButton onClick={() => removeItemFromPackage(pi.PackageItemID)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PackageManagement;

